import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

// Components
import { FullPageWidth } from '../../components/global/FullPageWidth/FullPageWidth.tsx';
import { BillboardBannerAd } from 'dibs-google-ad-manager/exports/BillboardBannerAd.ts';

// Helpers
import { getBillboardAdData } from 'dibs-google-ad-manager/exports/adHelpers.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useAbsentAdHandler } from 'dibs-google-ad-manager/exports/hooks.ts';
import { type AdBillboardBanner } from 'dibs-google-ad-manager/exports/types.ts';

import classNames from 'classnames';

import { type SbSharedBillboardAd_itemSearch$key } from './__generated__/SbSharedBillboardAd_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbSharedBillboardAd_itemSearch on ItemSearchQueryConnection {
        displayUriRef
    }
`;

type Props = {
    billboardAdData: AdBillboardBanner;
    className?: string;
};

const SbSharedBillboardAd: FC<Props> = ({ billboardAdData, className }) => {
    const { isAbsentAd, onAbsentAd } = useAbsentAdHandler(billboardAdData.elementId);

    return (
        <FullPageWidth className={classNames(!isAbsentAd && className)}>
            <BillboardBannerAd {...billboardAdData} lazyLoad onAbsentAd={onAbsentAd} />
        </FullPageWidth>
    );
};

type WrapperProps = {
    itemSearch: SbSharedBillboardAd_itemSearch$key;
    className?: string;
};

export const SbSharedBillboardAdWrapper: FC<WrapperProps> = ({
    itemSearch: itemSearchRef,
    className,
}) => {
    const { areDisplayAdsEnabled, isDesktop } = useServerVarsContext();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);

    if (!areDisplayAdsEnabled) {
        return null;
    }

    const deviceType = isDesktop ? 'desktop' : 'mobile';
    const displayUriRef = itemSearch?.displayUriRef ?? '';
    const billboardAdData = getBillboardAdData(displayUriRef)?.[deviceType];

    if (!billboardAdData) {
        return null;
    }

    return <SbSharedBillboardAd className={className} billboardAdData={billboardAdData} />;
};
